
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { InfoIcon } from "lucide-react";

interface RegionPickerProps {
  value: string;
  onChange: (value: string) => void;
  type: "country" | "business_area";
}

export const RegionPicker = ({ value, onChange, type }: RegionPickerProps) => {
  const { data: regions } = useQuery({
    queryKey: ["regions", type],
    queryFn: async () => {
      console.log(`Fetching regions of type: ${type}`);
      const { data, error } = await supabase
        .from("regions")
        .select("*")
        .eq("type", type)
        .order("name");
      
      if (error) {
        console.error("Error fetching regions:", error);
        throw error;
      }
      console.log(`Fetched regions for ${type}:`, data);
      return data || [];
    },
  });

  if (type === "country") {
    return (
      <div className="relative flex items-center gap-2">
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter your country..."
          className="w-full"
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Enter your country name (e.g., United Kingdom, United States, Germany)</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <div className="relative flex items-center gap-2">
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter your business area..."
        className="w-full"
      />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent className="max-w-[300px]">
            <p>Examples: B2B (Business to Business), B2C (Business to Consumer), D2C (Direct to Consumer), 
              SaaS, E-commerce, Marketplace, etc.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
